<template>
  <div class="container mb-6">
    <loader v-if="results === null" />
    <info-msg v-else-if="results.length === 0" message="Your search returned no results" />

    <panel v-else>
      <div class="flex">
        <div class="w-full lg:w-1/4 px-2">
          <select-input
            label="Duration"
            :options="durationOptions"
            v-model="attachment.registry_duration"
            option-key="name"
            class-name="mb-2"
          />
        </div>
        <div class="w-full lg:w-1/4 px-2">
          <date-picker-input v-model="attachment.registry_start_date" class-name="mb-0" auto-close label="Start Date" placeholder="Start Date"></date-picker-input>
        </div>
        <div class="w-full lg:w-1/2 px-2">
          <primary-btn class="mt-8 mr-8" @click="search" :loading="loading">
            Search Report
          </primary-btn>
          <secondary-btn class="mt-8" @click="clearFilter" :loading="loading">
            Reset Filter
          </secondary-btn>
        </div>
      </div>
      <land-registry-data-charts :data="results['totals']" :refreshed_at="results['refreshed_at']" :heading="marketFiguresHeading" :subheading="subheading"
                                 v-if="!loading" :figureOnly="figureOnly" :income-only="incomeOnly" @refresh="refreshSearch()" />
    </panel>
  </div>
</template>

<script>
import Vue from 'vue'
import api from '@/http'
import { mapMutations } from 'vuex'
import { debounce } from 'lodash'
import LandRegistryDataCharts from './LandRegistryDataCharts.vue'
import moment from 'moment'

export default {
  components: { LandRegistryDataCharts },
  props: {
    officeId: {
      type: Number,
      default: null
    },
    reviewId: {
      type: Number,
      default: null
    },
    figureOnly: {
      type: Boolean,
      default: false
    },
    incomeOnly: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object
    }
  },
  data () {
    return {
      results: null,
      refresh: false,
      loading: false,
      durationOptions: [
        { id: 'year', name: 'One Year' },
        { id: '6-months', name: 'Six Months' },
        { id: '3-months', name: 'Three Months' },
        { id: 'month', name: 'One Month' }
      ],
      attachment: this.value
    }
  },
  computed: {
    marketFiguresHeading () {
      if (this.results.date_range) {
        return 'Market Figures for ' + this.results.date_range
      }
      let start = new Date(this.attachment.registry_start_date)
      let end = new Date(this.attachment.registry_start_date)
      end.setFullYear(end.getFullYear() + 1)

      return 'Market Figures for ' + this.formatDateAsMonth(start) + ' to ' + this.formatDateAsMonth(end)
    },
    subheading () {
      if (this.results.min_price) {
        return 'Results include a minimum price filter of ' + Vue.filter('gbp')(this.results.min_price)
      }
    }
  },
  mounted () {
    if (!this.attachment.registry_duration) {
      this.attachment.registry_duration = 'year'
    }
    if (!this.attachment.registry_start_date) {
      this.setDefaultStartDate()
    }
    this.search()
  },
  methods: {
    ...mapMutations(['setErrorMessage', 'setSuccessMessage']),
    search: debounce(function () {
      this.loading = true
      let params = {}
      if (this.reviewId) {
        params = {
          review_id: this.reviewId,
          duration: this.attachment.registry_duration,
          start_date: this.attachment.registry_start_date,
          refresh_review_data: this.refresh
        }
      } else {
        params = {
          office_id: this.officeId,
          duration: 'year',
          start_date: this.registry_start_date
        }
      }
      api
        .get('/land-registry-data', {
          params: params
        })
        .then(
          response => {
            this.results = response.data
            this.loading = false
          },
          () => {
            this.setErrorMessage('Failed to load results')
            this.loading = false
          }
        )
    }, 500),

    refreshSearch () {
      this.results['refreshed_at'] = '(reloading...)'
      this.refresh = true
      this.search()
    },

    /**
     * Given a Date object, outputs a human-readable version of the year and month
     *
     * @param {Date} date The date to format.
     */
    formatDateAsMonth (date) {
      return date.toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric'
      })
    },
    clearFilter () {
      this.attachment.registry_duration = 'year'
      this.attachment.registry_start_date = null
      this.setDefaultStartDate()
    },
    setDefaultStartDate () {
      let now = moment()
      switch (this.attachment.registry_duration) {
        case 'year':
          let year = now.year() - 1
          this.attachment.registry_start_date = year + '-01-01'
          return
        case 'month':
          this.attachment.registry_start_date = now.subtract(1, 'month').format('YYYY-MM-DD')
          return
        case '3-months':
          this.attachment.registry_start_date = now.subtract(3, 'month').format('YYYY-MM-DD')
          return
        case '6-months':
          this.attachment.registry_start_date = now.subtract(6, 'month').format('YYYY-MM-DD')
      }
    }
  }
}
</script>
